import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, Sparkles, Play, Info } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Overline } from "../components/motion";
import { useTranslate } from "../context/TranslateContext";
import { CHART_LANGS, CHARTS } from "../mock/data";

export default function Charts() {
  const { t } = useTranslate();
  const [lang, setLang] = useState("India");
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const rows = CHARTS[lang] || [];
  const listenText = `CinemaPulse ${lang} chart. The top trending Indian film and independent music this week, ranked by streams, video views, search and fan votes.`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <div className="mx-auto max-w-[1100px] px-6 py-14 md:px-12 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5">
              <Sparkles className="h-4 w-4 text-gold" /><Overline className="!tracking-[0.2em]">CinemaPulse Charts</Overline>
            </div>
            <h1 className="mt-5 font-display text-5xl font-semibold tracking-tight text-cream md:text-7xl">
              {t("Top ")}<span className="italic text-gradient-gold">{t("trending")}</span>{t(" now")}
            </h1>
          </div>
          
        </div>

        {/* Language tabs */}
        <div className="mt-10 flex flex-wrap gap-2" data-testid="chart-tabs">
          {CHART_LANGS.map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`chart-tab-${l}`}
              className={`rounded-full border px-5 py-2 font-body text-sm transition-colors duration-200 ${lang === l ? "border-gold bg-gold text-ink" : "border-edge text-cream hover:border-gold"}`}>
              {t(l)} {t("Top")} {rows.length && l === lang ? "" : ""}
            </button>
          ))}
        </div>

        {/* Chart list */}
        <AnimatePresence mode="wait">
          <motion.div key={lang} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="mt-8 overflow-hidden rounded-2xl border border-edge bg-surface/40" data-testid="chart-list">
            {rows.map((r, idx) => (
              <motion.div key={r.rank} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.04 }}
                data-testid={`chart-row-${r.rank}`}
                className="flex items-center gap-4 border-b border-edge px-4 py-4 last:border-0 md:px-6">
                <div className="w-10 text-center font-display text-3xl font-semibold text-cream">{r.rank}</div>
                <Delta delta={r.delta} />
                <div className="min-w-0 flex-1">
                  {r.songId ? (
                    <Link to={`/song/${r.songId}`} className="block truncate font-body text-base text-cream hover:text-gold md:text-lg">{r.title}</Link>
                  ) : (
                    <span className="block truncate font-body text-base text-cream md:text-lg">{r.title}</span>
                  )}
                  <span className="block truncate font-body text-sm text-ash">{r.artist} · {r.film === "Indie" ? t("Independent") : r.film}</span>
                </div>
                <span className="hidden font-body text-xs text-ash sm:block">{r.weeks} {t("wks")}</span>
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-edge text-gold"><Play className="h-3.5 w-3.5 fill-current" /></span>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-edge px-4 py-2 font-body text-xs text-ash" data-testid="chart-methodology">
          <Info className="h-3.5 w-3.5" /> {t("Ranked by streams, video views, search trends and fan votes. Updated weekly.")}
        </div>
      </div>
    </div>
  );
}

const Delta = ({ delta }) => {
  const { t } = useTranslate();
  if (delta === "new") return <span className="rounded px-1.5 py-0.5 text-[0.6rem] font-medium uppercase tracking-wider text-emerald2" data-testid="delta-new">{t("New")}</span>;
  if (delta > 0) return <span className="flex items-center gap-0.5 text-emerald2"><TrendingUp className="h-4 w-4" /><span className="font-body text-xs">{delta}</span></span>;
  if (delta < 0) return <span className="flex items-center gap-0.5 text-crimson"><TrendingDown className="h-4 w-4" /><span className="font-body text-xs">{Math.abs(delta)}</span></span>;
  return <span className="text-ash"><Minus className="h-4 w-4" /></span>;
};
